import React from "react";
import { motion } from "framer-motion";
import { Target } from "lucide-react";

interface LeadPipelineWidgetProps {
  pipeline: { status: string; count: number }[];
}

const statusConfig: Record<
  string,
  { bg: string; text: string; dot: string }
> = {
  new: { bg: "bg-blue-50", text: "text-blue-700", dot: "bg-blue-500" },
  contacted: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  qualified: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    dot: "bg-emerald-500",
  },
  converted: {
    bg: "bg-green-50",
    text: "text-green-700",
    dot: "bg-green-600",
  },
  lost: { bg: "bg-slate-50", text: "text-slate-600", dot: "bg-slate-400" },
};

const getStatusConfig = (status: string) =>
  statusConfig[status.toLowerCase()] || {
    bg: "bg-slate-50",
    text: "text-slate-600",
    dot: "bg-slate-400",
  };

const LeadPipelineWidget: React.FC<LeadPipelineWidgetProps> = ({
  pipeline,
}) => {
  const totalLeads = pipeline.reduce((s, p) => s + p.count, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div
            className="p-2 rounded-lg"
            style={{ backgroundColor: "#0F51321A" }}
          >
            <Target className="h-4 w-4" style={{ color: "#0F5132" }} />
          </div>
          <h3 className="text-sm font-semibold text-slate-700 m-0 leading-none">
            Leads Pipeline
          </h3>
        </div>
        {totalLeads > 0 && (
          <span className="text-xs font-medium text-slate-500">
            {totalLeads} total
          </span>
        )}
      </div>

      {!pipeline || pipeline.length === 0 || totalLeads === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="h-12 w-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
            <Target className="h-6 w-6 text-slate-300" />
          </div>
          <p className="font-medium text-slate-500 text-sm">
            No leads in pipeline
          </p>
          <p className="text-xs text-slate-400">
            Add leads to track your pipeline
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Status badges row */}
          <div className="flex flex-wrap gap-2">
            {pipeline.map((item) => {
              const config = getStatusConfig(item.status);
              return (
                <div
                  key={item.status}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${config.bg}`}
                >
                  <div
                    className={`h-2 w-2 rounded-full ${config.dot}`}
                  />
                  <span
                    className={`text-sm font-medium capitalize ${config.text}`}
                  >
                    {item.status}
                  </span>
                  <span
                    className={`text-sm font-bold ${config.text}`}
                  >
                    {item.count}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Visual bar breakdown */}
          <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
            {pipeline
              .filter((p) => p.count > 0)
              .map((item) => {
                const config = getStatusConfig(item.status);
                const pct = (item.count / totalLeads) * 100;
                return (
                  <motion.div
                    key={item.status}
                    className={`${config.dot}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    title={`${item.status}: ${item.count}`}
                  />
                );
              })}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default LeadPipelineWidget;
